/**
 * 系统设置 · 客服与收款（PAY-1）· 样稿 designs/mockups/site-payments/官网收款-样稿.html（v-svc）
 * 客服：老板 / 技术管理员可改（手机站咨询面板、详情页、页脚读这里）。
 * 收款码：🔴 只有老板 / 技术管理员能改（settings.payment_qr.manage；公司站暂无老板账号，用户 10-09 定先由技术管理员放码）——换成别人的码，客人的钱就付到别人那里去了：
 *   改的时候勾「确认是公司账户」· 改完铃铛老板 + 财务 · 旧码留记录。其它角色看不到码、也不发请求。
 * 接口：GET|PATCH /api/v1/settings/site-contact · PUT …/site-contact/wechat-qr · GET|PUT /api/v1/settings/payment-qr/{wechat|alipay} · GET …/history
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCan } from "@/auth/permissions";
import { apiConfig } from "@/services/api";
import { PageShell, LoadingState, ErrorState } from "@/shared/ui/PageHeader";
import { fmtTime, sitePaymentsApi, type PaymentQrInfo, type ZelleInfo } from "@/services/sitePayments";
import { Modal, errText } from "@/pages/sitePayments/ui";
import { SettingsTabs } from "./SettingsTabs";

const img = (u: string) => (u.startsWith("http") ? u : `${apiConfig.base}${u}`);
type ContactKey = "contact_wechat" | "contact_phone";

export function SiteServiceSettingsPage() {
  const canEdit = useCan("system.settings.update");
  const canQr = useCan("settings.payment_qr.manage");
  const qc = useQueryClient();
  const c = useQuery({ queryKey: ["settings", "site-contact"], queryFn: sitePaymentsApi.contact });
  const [edit, setEdit] = useState<ContactKey | null>(null);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (p: { contact_wechat?: string; contact_phone?: string }) => sitePaymentsApi.patchContact(p),
    onSuccess: () => { setEdit(null); setErr(null); qc.invalidateQueries({ queryKey: ["settings", "site-contact"] }); },
    onError: (e) => setErr(errText(e)),
  });
  const upload = useMutation({
    mutationFn: (f: File) => sitePaymentsApi.putContactQr(f),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "site-contact"] }),
    onError: (e) => setErr(errText(e)),
  });

  if (c.isError) return <ErrorState message="客服设置加载失败" onRetry={() => c.refetch()} />;
  if (!c.data) return <LoadingState />;
  const d = c.data;

  const field = (k: ContactKey, label: string, value: string) => (
    <div className="rounded-xl border border-slate-200 px-3.5 py-3">
      <div className="mb-1 text-[12px] text-slate-400">{label}</div>
      {edit === k ? (
        <div className="flex gap-2">
          <input autoFocus aria-label={label} value={draft} onChange={(e) => setDraft(e.target.value)} className="h-8 flex-1 rounded-lg border border-slate-300 px-2 text-[13px]" />
          <button type="button" disabled={save.isPending} onClick={() => save.mutate({ [k]: draft })} className="rounded-lg bg-blue-600 px-3 text-[12px] font-semibold text-white">保存</button>
          <button type="button" onClick={() => setEdit(null)} className="rounded-lg border border-slate-300 px-3 text-[12px]">取消</button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-[13.5px] font-semibold">
          {value || <span className="font-normal text-slate-400">未设置（手机站不显示这一项）</span>}
          {canEdit && <button type="button" onClick={() => { setDraft(value); setEdit(k); }} className="rounded-lg border border-slate-300 px-2 py-0.5 text-[12px] font-normal hover:bg-slate-50">改</button>}
        </div>
      )}
    </div>
  );

  return (
    <PageShell title="系统设置 · 客服与收款" subtitle="手机站咨询面板、详情页、页脚的客服信息，以及客人扫码付款用的收款码">
      <SettingsTabs />
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="text-base font-semibold">客服 <span className="ml-1 text-[12px] font-normal text-slate-500">老板、技术管理员可改 · 手机站咨询面板、详情页、页脚读这里</span></h3>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          {field("contact_wechat", "客服微信号", d.contact_wechat)}
          {field("contact_phone", "客服电话", d.contact_phone)}
          <div className="rounded-xl border border-slate-200 px-3.5 py-3">
            <div className="mb-1 text-[12px] text-slate-400">客服微信二维码</div>
            <div className="flex items-center gap-3">
              {d.contact_wechat_qr_url
                ? <img src={img(d.contact_wechat_qr_url)} alt="客服微信二维码" className="h-16 w-16 rounded-lg border border-slate-200 object-cover" />
                : <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-[11px] text-slate-400">未设置</div>}
              {canEdit && (
                <label className="cursor-pointer rounded-lg border border-slate-300 px-2.5 py-1 text-[12px] hover:bg-slate-50">
                  {upload.isPending ? "上传中…" : "换图"}
                  <input type="file" accept="image/*" aria-label="上传客服微信二维码" className="hidden" disabled={upload.isPending}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }} />
                </label>
              )}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 px-3.5 py-3">
            <div className="mb-1 text-[12px] text-slate-400">客服时间</div>
            <div className="text-[13.5px] font-semibold">{d.service_hours}</div>
          </div>
        </div>
        {err && <div className="mt-3 text-[12.5px] text-rose-600">{err}</div>}
      </section>
      <PaymentQrCard allowed={canQr} />
    </PageShell>
  );
}

function PaymentQrCard({ allowed }: { allowed: boolean }) {
  const q = useQuery({ queryKey: ["settings", "payment-qr"], queryFn: sitePaymentsApi.paymentQr, enabled: allowed });
  const [dlg, setDlg] = useState<"wechat" | "alipay" | null>(null);
  const [zelleDlg, setZelleDlg] = useState(false);
  return (
    <section data-testid="payment-qr-card" className="rounded-xl border border-rose-200 bg-white p-6">
      <h3 className="flex items-center gap-2 text-base font-semibold">收款码 <span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-[12px] font-medium text-rose-600">只有老板 / 技术管理员能改</span></h3>
      <p className="mt-1 text-[12px] text-slate-400">手机站扫码付款用。🔴 收款码被换成别人的码，客人的钱就付到别人那里去了，所以：只有老板 / 技术管理员能改 · 改的时候再确认一次 · 改完马上铃铛通知老板和财务（谁、什么时候）· 旧码留记录</p>
      {!allowed ? (
        <div data-testid="payment-qr-locked" className="mt-4 rounded-lg bg-slate-50 px-3 py-3 text-[13px] text-slate-500">收款码只有老板 / 技术管理员能查看和更换。需要更换请联系老板或技术管理员。</div>
      ) : q.isError ? (
        <ErrorState message="收款码加载失败" onRetry={() => q.refetch()} />
      ) : !q.data ? (
        <LoadingState />
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          {(["wechat", "alipay"] as const).map((m) => <QrField key={m} method={m} info={q.data[m]} onChange={() => setDlg(m)} />)}
          <ZelleField info={q.data.zelle} onChange={() => setZelleDlg(true)} />
        </div>
      )}
      {zelleDlg && <ZelleDialog current={q.data?.zelle?.account ?? ""} onClose={() => setZelleDlg(false)} />}
      {dlg && <ChangeQrDialog method={dlg} label={q.data?.[dlg].label ?? ""} onClose={() => setDlg(null)} />}
    </section>
  );
}

function QrField({ method, info, onChange }: { method: "wechat" | "alipay"; info: PaymentQrInfo; onChange: () => void }) {
  const [showHist, setShowHist] = useState(false);
  const hist = useQuery({ queryKey: ["settings", "payment-qr", method, "history"], queryFn: () => sitePaymentsApi.paymentQrHistory(method), enabled: showHist });
  return (
    <div className="rounded-xl border border-slate-200 px-3.5 py-3">
      <div className="mb-1 text-[12px] text-slate-400">公司{info.label}收款码</div>
      <div className="flex items-center gap-3">
        {info.url
          ? <img src={img(info.url)} alt={`${info.label}收款码`} className="h-16 w-16 rounded-lg border border-slate-200 object-cover" />
          : <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-[11px] text-slate-400">未设置</div>}
        <button type="button" onClick={onChange} className="rounded-lg border border-slate-300 px-2.5 py-1 text-[12px] hover:bg-slate-50">换图</button>
        <span className="text-[12px] text-slate-500">{info.last_changed_at ? `上次修改：${info.last_changed_by ?? "—"} ${fmtTime(info.last_changed_at)}` : "还没改过"}</span>
      </div>
      <button type="button" onClick={() => setShowHist((v) => !v)} className="mt-2 text-[12px] text-blue-600 hover:underline">{showHist ? "收起更换记录" : "更换记录"}</button>
      {showHist && (
        <ul className="mt-1 text-[12px] text-slate-600">
          {(hist.data ?? []).map((h, i) => (
            <li key={i}>{fmtTime(h.changed_at)} · {h.changed_by ?? "—"} 换了码{h.old_url ? <> · <a className="text-blue-600 underline" href={img(h.old_url)} target="_blank" rel="noreferrer">旧码</a></> : null}</li>
          ))}
          {hist.data?.length === 0 && <li className="text-slate-400">没有更换记录</li>}
        </ul>
      )}
    </div>
  );
}

/** 用户 10-29 · Zelle 收款账号（邮箱 / 手机号文字，手工散客付款页显示 + 复制）。和收款码同规矩：只有老板 / 技术管理员能改、再确认、铃铛、留记录；没设就不显示 Zelle。 */
function ZelleField({ info, onChange }: { info: ZelleInfo | undefined; onChange: () => void }) {
  const [showHist, setShowHist] = useState(false);
  const hist = useQuery({ queryKey: ["settings", "payment-qr", "zelle", "history"], queryFn: () => sitePaymentsApi.paymentQrHistory("zelle"), enabled: showHist });
  return (
    <div className="rounded-xl border border-slate-200 px-3.5 py-3 md:col-span-2" data-testid="zelle-field">
      <div className="mb-1 text-[12px] text-slate-400">公司 Zelle 收款账号（邮箱或手机号）· 手工订单的客人付款页显示</div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[14px] font-semibold text-slate-800" data-testid="zelle-account">{info?.account || "未设置（付款页不显示 Zelle）"}</span>
        <button type="button" onClick={onChange} data-testid="zelle-change" className="rounded-lg border border-slate-300 px-2.5 py-1 text-[12px] hover:bg-slate-50">{info?.account ? "修改" : "设置"}</button>
        <span className="text-[12px] text-slate-500">{info?.last_changed_at ? `上次修改：${info.last_changed_by ?? "—"} ${fmtTime(info.last_changed_at)}` : "还没改过"}</span>
      </div>
      <button type="button" onClick={() => setShowHist((v) => !v)} className="mt-2 text-[12px] text-blue-600 hover:underline">{showHist ? "收起更换记录" : "更换记录"}</button>
      {showHist && (
        <ul className="mt-1 text-[12px] text-slate-600">
          {(hist.data ?? []).map((h, i) => <li key={i}>{fmtTime(h.changed_at)} · {h.changed_by ?? "—"} · {h.old_url ?? "（无）"} → {h.new_url}</li>)}
          {hist.data?.length === 0 && <li className="text-slate-400">没有更换记录</li>}
        </ul>
      )}
    </div>
  );
}

function ZelleDialog({ current, onClose }: { current: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [acct, setAcct] = useState(current);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => sitePaymentsApi.putZelle(acct.trim(), ok),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings", "payment-qr"] }); onClose(); },
    onError: (e) => setErr(errText(e)),
  });
  return (
    <Modal title="🔴 设置 Zelle 收款账号" onClose={onClose}>
      <div className="text-[12.5px] text-slate-500">改成新账号以后，客人用 Zelle 付款会转到新账号。请确认这是公司自己的账户。留空保存 = 清除（付款页不再显示 Zelle）。</div>
      <input aria-label="Zelle 收款账号" data-testid="zelle-input" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[13px]" placeholder="邮箱或手机号" value={acct} onChange={(e) => { setAcct(e.target.value); setErr(null); }} />
      <label className="flex items-center gap-1.5 text-[13px]"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} />我确认这是公司自己的收款账户</label>
      <div className="text-[12px] text-slate-400">换完马上铃铛通知老板和财务（谁、什么时候换的）；旧值留记录。</div>
      {err && <div className="text-[12.5px] text-rose-600" role="alert">{err}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-3 py-1.5 text-[13px]">取消</button>
        <button type="button" disabled={!ok || m.isPending} onClick={() => m.mutate()} data-testid="zelle-save" className="rounded-lg bg-rose-600 px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-40">{m.isPending ? "保存中…" : "确认保存"}</button>
      </div>
    </Modal>
  );
}

function ChangeQrDialog({ method, label, onClose }: { method: "wechat" | "alipay"; label: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => sitePaymentsApi.putPaymentQr(method, file as File, ok),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings", "payment-qr"] }); onClose(); },
    onError: (e) => setErr(errText(e)),
  });
  return (
    <Modal title={`🔴 更换${label}收款码`} onClose={onClose}>
      <div className="text-[12.5px] text-slate-500">换成新码以后，客人付款会付到新码对应的账户。请确认新码是公司账户。</div>
      <label className="cursor-pointer rounded-xl border-[1.5px] border-dashed border-slate-300 p-6 text-center text-[13px] text-slate-400">
        {file ? file.name : "上传新收款码图片"}
        <input type="file" accept="image/*" aria-label="上传新收款码图片" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </label>
      <label className="flex items-center gap-1.5 text-[13px]"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} />我确认这是公司自己的收款账户</label>
      <div className="text-[12px] text-slate-400">换完马上铃铛通知老板和财务（谁、什么时候换的）；旧码留记录。</div>
      {err && <div className="text-[12.5px] text-rose-600">{err}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-3 py-1.5 text-[13px]">取消</button>
        <button type="button" disabled={!file || !ok || m.isPending} onClick={() => m.mutate()} className="rounded-lg bg-rose-600 px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-40">{m.isPending ? "更换中…" : "确认更换"}</button>
      </div>
    </Modal>
  );
}
